"use client";

import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import Link from "next/link";
import Image from "next/image";
import useStore from "@/store/useStore";
import {
  useDebounce,
  AnimatedGrid,
  AnimatedGridItem,
  SearchWrapper,
  AnimatedAccordion,
} from "@/components/ui/animated-grid";

export default function FAQPage() {
  const { session } = useStore();
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [openSections, setOpenSections] = useState({});
  const [isSearching, setIsSearching] = useState(false);

  const isAuthenticated = !!session?.user;

  // Show searching state when search query changes
  useEffect(() => {
    if (searchQuery !== debouncedSearchQuery) {
      setIsSearching(true);
    } else {
      setIsSearching(false);
    }
  }, [searchQuery, debouncedSearchQuery]);

  const toggleSection = (sectionId) => {
    setOpenSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const faqData = [
    {
      id: "getting-started",
      title: "Getting Started",
      questions: [
        {
          q: "What is Qensa?",
          a: "Qensa is a personal website builder that allows you to create beautiful, customizable web pages called 'spaces' in just 5 minutes. It's designed for everyone - from individuals to businesses - who want to establish their online presence quickly and easily.",
        },
        {
          q: "How do I create my first space?",
          a: "Getting started is easy! Sign up for a free account, click 'Create Space', choose a template or start from scratch, add your content using our drag-and-drop widgets, customize your theme, and publish when ready. The whole process takes about 5 minutes.",
        },
        {
          q: "What can I use Qensa for?",
          a: "Qensa is incredibly versatile! You can create personal portfolios, business pages, link-in-bio pages, event pages, online resumes, product showcases, artist galleries, and much more. Any time you need a beautiful web presence, Qensa is the perfect solution.",
        },
        {
          q: "Do I need technical skills to use Qensa?",
          a: "Not at all! Qensa is designed for everyone, regardless of technical ability. Our intuitive drag-and-drop interface, pre-designed templates, and easy customization options mean you can create professional-looking spaces without any coding knowledge.",
        },
      ],
    },
    {
      id: "widgets",
      title: "Widgets & Features",
      questions: [
        {
          q: "What widgets are available?",
          a: "Qensa offers a wide variety of widgets including: Bio (profile information), Rich Text (formatted content), Links (custom link buttons), Mini Links (compact link grid), Image Gallery, Single Media (image/video), Video Player, File Downloads, Business Hours, Location/Map, Spotify integration, Apple Music integration, Calendly scheduling, OpenTable reservations, and Divider (section breaks).",
        },
        {
          q: "Can I customize widgets?",
          a: "Yes! Each widget is fully customizable. You can adjust colors, fonts, spacing, borders, shadows, and more. Many widgets also have specific configuration options - for example, the map widget lets you set custom locations and styles, while media widgets allow captions and lightbox effects.",
        },
        {
          q: "How do I add a widget to my space?",
          a: "Simply click the '+' button in the editor to open the widget selector. Browse by category or use the search function to find the widget you need. Click on any widget to add it to your space, then drag it to position it exactly where you want.",
        },
        {
          q: "Are there limits on how many widgets I can add?",
          a: "No! You can add as many widgets as you need to create your perfect space. The responsive grid system automatically organizes your widgets to look great on all devices.",
        },
      ],
    },
    {
      id: "customization",
      title: "Customization & Themes",
      questions: [
        {
          q: "How can I customize my space's appearance?",
          a: "Qensa offers extensive customization options: Choose from beautiful background images or upload your own, select solid colors or gradients, pick from multiple font combinations, adjust spacing and layout, customize individual widget styles, and create a cohesive look with theme presets.",
        },
        {
          q: "Can I use my own images and media?",
          a: "Absolutely! You can upload your own images for backgrounds, widget content, profile pictures, and more. We support common image formats (JPG, PNG, GIF) and video files. You can also use images from Unsplash directly within the platform.",
        },
        {
          q: "Are the designs mobile-responsive?",
          a: "Yes! Every space is automatically optimized for all devices. Our responsive grid system ensures your content looks perfect on desktops, tablets, and mobile phones. You can preview how your space looks on different devices while editing.",
        },
        {
          q: "Can I change my space URL/slug?",
          a: "Yes, you can customize your space URL when creating it. Your space will be accessible at qensa.com/yourslug. Choose something memorable and relevant to your content. Some restrictions apply to ensure URLs are unique and appropriate.",
        },
      ],
    },
    {
      id: "privacy-sharing",
      title: "Privacy & Sharing",
      questions: [
        {
          q: "Can I password protect my space?",
          a: "Yes! You can add password protection to any space through the Privacy settings. Only visitors with the correct password will be able to view your space. This is perfect for private portfolios or exclusive content.",
        },
        {
          q: "Can I prevent search engines from indexing my space?",
          a: "Yes, you can enable the 'Prevent search engine crawling' option in Privacy settings. This tells search engines like Google not to index your space, keeping it more private while still accessible via direct link.",
        },
        {
          q: "How do I share my space?",
          a: "Each space has a unique URL (qensa.com/yourslug) that you can share anywhere. Use the share button in the editor to quickly copy your link. Your space is automatically optimized for social media sharing with custom preview images and descriptions.",
        },
        {
          q: "Can multiple people edit the same space?",
          a: "This feature is coming soon! We're working on collaborative editing that will allow you to invite others to help manage your spaces. For now, each space has one owner/editor.",
        },
      ],
    },
    {
      id: "features",
      title: "Advanced Features",
      questions: [
        {
          q: "Can I see analytics for my space?",
          a: "Yes! Every space includes built-in analytics showing page views, unique visitors, referral sources, and more. Access analytics through your space dashboard to understand your audience better.",
        },
        {
          q: "Can I use a custom domain?",
          a: "Custom domain support is on our roadmap. Currently, all spaces use the qensa.com domain with your custom slug. This ensures fast, reliable hosting for all users.",
        },
        {
          q: "Is there an API available?",
          a: "API access is planned for future release. This will allow developers to programmatically create and manage spaces. Stay tuned for updates!",
        },
        {
          q: "Can I export my space?",
          a: "While direct export isn't currently available, all your content remains accessible and you own all your data. We're exploring export options for future releases.",
        },
        {
          q: "Are there storage limits?",
          a: "We provide generous storage for images and files. Most users never hit limits. If you have special requirements for large media files, contact our support team.",
        },
      ],
    },
    {
      id: "account",
      title: "Account & Security",
      questions: [
        {
          q: "How do I reset my password?",
          a: "Click 'Forgot Password' on the sign-in page. Enter your email address and we'll send you a secure link to reset your password. The link expires after 24 hours for security.",
        },
        {
          q: "Can I change my email address?",
          a: "Yes, you can update your email address in Account Settings. You'll need to verify the new email address before the change takes effect.",
        },
        {
          q: "Is my data secure?",
          a: "Absolutely! We use industry-standard encryption for all data transmission and storage. Your content is stored securely on enterprise-grade infrastructure. We never share your personal information with third parties.",
        },
        {
          q: "Can I delete my account?",
          a: "Yes, you can delete your account at any time from Account Settings. This will permanently remove your account and all associated spaces. We recommend downloading any important content before deletion.",
        },
        {
          q: "What authentication methods are supported?",
          a: "You can sign in with email/password or use social authentication with Google or GitHub. All methods are equally secure and give you full access to all features.",
        },
      ],
    },
    {
      id: "troubleshooting",
      title: "Troubleshooting",
      questions: [
        {
          q: "My changes aren't saving. What should I do?",
          a: "First, check your internet connection. Qensa auto-saves your changes, indicated by the save status in the editor. If problems persist, try refreshing the page. Your recent changes are preserved in local storage as a backup.",
        },
        {
          q: "Why can't I publish my space?",
          a: "Make sure you're signed in and have verified your email address. If you've already used your free trial, you'll need to add payment information. Check that your chosen slug is available and meets our guidelines.",
        },
        {
          q: "Images aren't loading properly. How do I fix this?",
          a: "Ensure your images are in supported formats (JPG, PNG, GIF) and under 10MB. If uploading fails, try a smaller file size or different format. Clear your browser cache if images appear broken.",
        },
        {
          q: "Can I recover a deleted space?",
          a: "Deleted spaces are moved to trash and can be recovered within 30 days. After that, deletion is permanent. Always keep backups of important content.",
        },
        {
          q: "The editor is running slowly. What can I do?",
          a: "Try using a modern browser (Chrome, Firefox, Safari, Edge) with the latest updates. Close unnecessary browser tabs and extensions. For best performance, avoid having too many high-resolution images on a single space.",
        },
      ],
    },
  ];

  // Filter FAQ data based on debounced search query
  const filteredFaqData = debouncedSearchQuery
    ? faqData
        .map((section) => ({
          ...section,
          questions: section.questions.filter(
            (q) =>
              q.q.toLowerCase().includes(debouncedSearchQuery.toLowerCase()) ||
              q.a.toLowerCase().includes(debouncedSearchQuery.toLowerCase())
          ),
        }))
        .filter((section) => section.questions.length > 0)
    : faqData;

  const hasResults = filteredFaqData.length > 0;

  return (
    <div className="min-h-screen bg-dark-deeper">
      {/* Header */}
      <header className="border-b border-dark-elevated bg-dark-deeper">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex items-center space-x-2">
              <Image
                src="/logos/beta/white.svg"
                alt="Qensa Logo"
                width={100}
                height={100}
              />
            </Link>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <Link
                href="/qonnect"
                className="text-neutral-light/70 hover:text-neutral-light transition-colors"
              >
                QONNECT
              </Link>
              <Link href="/faq" className="text-accent-lime font-medium">
                FAQ
              </Link>
              <Link
                href="/beta-program"
                className="text-neutral-light/70 hover:text-accent-lime transition-colors"
              >
                BETA
              </Link>
            </nav>

            {/* Right side */}
            <div className="flex items-center space-x-4">
              {isAuthenticated ? (
                <Link href="/user/spaces">
                  <div className="w-12 h-12 rounded-full bg-neutral-light/20 hover:bg-neutral-light/30 flex items-center justify-center transition-colors">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      className="w-6 h-6 text-neutral-light"
                    >
                      <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                      <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                    </svg>
                  </div>
                </Link>
              ) : (
                <Link
                  href="/onboarding"
                  className="border border-neutral-light text-neutral-light px-4 py-2 rounded-lg font-medium hover:bg-neutral-light hover:text-dark-deeper transition-colors"
                >
                  Create your Qensa
                </Link>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-dark-deeper py-16">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h1 className="text-5xl md:text-6xl font-bold text-neutral-light mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-xl text-neutral-light/70 mb-12">
            Everything you need to know about Qensa
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-4">
            <div className="relative">
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-light/50 w-5 h-5"
              />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-4 text-lg bg-dark-surface border border-dark-elevated rounded-xl text-neutral-light placeholder:text-neutral-light/50 focus:border-accent-lime focus:outline-none focus:ring-2 focus:ring-accent-lime/20 transition-all duration-200"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-neutral-light/50 hover:text-neutral-light transition-colors"
                >
                  <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="bg-dark-deeper py-6">
        <div className="max-w-7xl mx-auto px-6">
          {/* Quick Access - Only show when not searching */}
          {!searchQuery && (
            <AnimatedGridItem
              layoutId="quick-access"
              className="mb-8 p-4 bg-dark-surface rounded-lg border border-dark-elevated max-w-4xl mx-auto"
            >
              <h3 className="text-sm font-medium text-neutral-light/60 mb-3">
                Quick Access
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <button
                  onClick={() => {
                    setOpenSections({ "getting-started": true });
                    document
                      .getElementById("getting-started")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-left p-2 rounded hover:bg-neutral-light/5 transition-colors"
                >
                  <span className="text-sm text-neutral-light">
                    🚀 Getting Started
                  </span>
                </button>
                <button
                  onClick={() => {
                    setOpenSections({ widgets: true });
                    document
                      .getElementById("widgets")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-left p-2 rounded hover:bg-neutral-light/5 transition-colors"
                >
                  <span className="text-sm text-neutral-light">🔧 Widgets</span>
                </button>
                <button
                  onClick={() => {
                    setOpenSections({ troubleshooting: true });
                    document
                      .getElementById("troubleshooting")
                      ?.scrollIntoView({ behavior: "smooth" });
                  }}
                  className="text-left p-2 rounded hover:bg-neutral-light/5 transition-colors"
                >
                  <span className="text-sm text-neutral-light">
                    🛠️ Troubleshooting
                  </span>
                </button>
              </div>
            </AnimatedGridItem>
          )}

          {/* FAQ Sections */}
          <SearchWrapper
            isSearching={isSearching}
            hasResults={hasResults}
            searchQuery={debouncedSearchQuery}
            emptyMessage="No FAQs found"
          >
            <AnimatedGrid className="space-y-6 max-w-5xl mx-auto">
              {filteredFaqData.map((section, index) => (
                <AnimatedGridItem
                  key={section.id}
                  layoutId={`faq-section-${section.id}`}
                  delay={index}
                  className="bg-dark-surface rounded-lg border border-dark-elevated overflow-hidden"
                >
                  <div id={section.id}>
                    <button
                      onClick={() => toggleSection(section.id)}
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-neutral-light/5 transition-colors"
                    >
                      <h2 className="text-xl font-semibold text-neutral-light">
                        {section.title}
                      </h2>
                      <FontAwesomeIcon
                        icon={
                          openSections[section.id] ? faChevronUp : faChevronDown
                        }
                        className="text-neutral-light/60"
                      />
                    </button>

                    <AnimatedAccordion isOpen={openSections[section.id]}>
                      <div className="px-6 pb-4 space-y-4">
                        {section.questions.map((item, questionIndex) => (
                          <div
                            key={questionIndex}
                            className="border-t border-dark-elevated pt-4"
                          >
                            <h3 className="font-medium text-neutral-light mb-2">
                              {item.q}
                            </h3>
                            <p className="text-neutral-light/60 leading-relaxed">
                              {item.a}
                            </p>
                          </div>
                        ))}
                      </div>
                    </AnimatedAccordion>
                  </div>
                </AnimatedGridItem>
              ))}
            </AnimatedGrid>
          </SearchWrapper>

          {/* Contact Section */}
          <AnimatedGridItem
            layoutId="contact-section"
            className="mt-12 text-center p-8 bg-dark-surface rounded-lg border border-dark-elevated max-w-4xl mx-auto"
          >
            <h3 className="text-2xl font-semibold text-neutral-light mb-4">
              Still have questions?
            </h3>
            <p className="text-neutral-light/60 mb-6 max-w-2xl mx-auto">
              Our support team is here to help. Whether you need assistance
              getting started, have technical questions, or want to learn about
              advanced features, we're just a message away.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                href="mailto:contact@qensa.com"
                className="inline-flex items-center px-6 py-3 bg-accent-lime hover:bg-accent-lime/90 text-dark-deeper rounded-lg transition-colors font-medium"
              >
                Email Support
              </Link>
            </div>
          </AnimatedGridItem>
        </div>
      </main>
    </div>
  );
}
